import { Component, createContext, useContext, type ReactNode } from "react";
import {
  removeAxiosInterceptor,
  setAxiosInterceptor,
} from "../utilization/AxiosUtilization";
import { createActivityReviewBySourceTypeAndSourceIdAndActivityIdAndActivityTemplateId } from "../requests/CatchtivityRequests";
import { clearImpersonationState } from "../utilization/ImpersonationSessionUtilization";
import { dispatchAiRefusalReset } from "../utilization/AiRefusalUtilization";

// Context value shape is intentionally `any` during the conversion — UserContext
// has ~120 fields/setters and modeling each precisely is a Phase 7 task. The
// `useAuth()` consumer pattern already throws when the value is null, so the
// runtime contract is "non-null when read".
export const UserContext = createContext<any>(null);

interface IUserContextProviderProps {
  children?: ReactNode;
}

class UserContextProvider extends Component<IUserContextProviderProps, any> {
  constructor(props: IUserContextProviderProps) {
    super(props);
    this.state = {
      userInformation: null,
      language: localStorage.getItem("language")
        ? localStorage.getItem("language")
        : "TR",
      categoryDTOList: [],
      categoryDTOListAsTree: [],
      skillDTOList: [],
      skillDTOListAsTree: [],
      distinctVersionCodeLevelList: [],
      userProfileDTOList: [],
      userProfile: null,
      userProfileSeason: null,
      userProfileGrade: null,
      userProfileBranch: null,
      userProfileInstitution: null,
      branchAssignmentDTOList: [],
      branchAssignmentListLoading: true,
      supportAssignmentDTOList: [],
      supportAssignmentListLoading: true,
      standardExamApplicationDTOList: [],
      standardExamApplicationListLoading: true,
      activeContentList: [],
      passiveContentList: [],
      // Previous (expired) support assignments + one-shot "already fetched" guards for
      // the two /practise previous-lists, so navigating back to /practise reuses
      // the cached data instead of re-firing the requests. See PractiseNowScreen.
      previousSupportAssignmentList: [],
      passiveContentListLoaded: false,
      previousSupportAssignmentListLoaded: false,
      personalizedAssignmentDTOList: [],
      personalizedAssignmentListLoading: true,
      activePersonalizedAssignmentList: [],
      passivePersonalizedAssignmentList: [],
      branchAssignmentPerformanceLoading: true,
      branchAssignmentPerformance: null,
      learnerHandwrittenExamPerformanceLoading: true,
      learnerHandwrittenExamPerformance: null,
      learnerMockExamPerformanceLoading: true,
      learnerMockExamPerformance: null,
      // Destek ödevi performance (internalPerformance.supportAssignmentItems). Took the optical exam's
      // slot when that kind was retired from the reports.
      learnerSupportAssignmentPerformanceLoading: true,
      learnerSupportAssignmentPerformance: null,
      personalizedAssignmentPerformanceLoading: true,
      personalizedAssignmentPerformance: null,
      branchAssignmentReportDetailListLoading: true,
      branchAssignmentReportDetailDTOList: [],
      learnerCompletionOutcomeReportMap: {},
      learnerHandwrittenOutcomeReportMap: {},
      learnerMockExamOutcomeReportMap: {},
      learnerSupportAssignmentOutcomeReportMap: {},
      // The category list the REPORTS read, which is deliberately NOT level-filtered — unlike
      // categoryDTOList above. See the comment at its build site in LearnerApp: a learner can be
      // assessed on an off-level curriculum version, and level-filtering silently drops those
      // outcomes. Ön koşullar make this load-bearing rather than an edge case, since a prerequisite
      // normally sits a grade or more BELOW the learner's own.
      reportCategoryDTOList: [],
      branchAssignmentCategoryScoreList: [],
      reportDTOList: [],
      individualModelDTOList: [],
      individualModelListLoading: true,
      availableFrameworkAnnualPlanList: [],
      frameworkAnnualPlanMap: {},
      frameworkAnnualPlanFileMap: {},
      availablePlanListLoading: true,
      // Map<coterieType, [categoryId, ...]> of outcome ids drawn from each plan's weeks
      // whose week.beginAt <= now() (i.e., already-started weeks — current ongoing + all
      // previous). Drives which outcomes appear on the Learning Path and which coterie pills
      // the learner sees. Empty until plans load; an empty map after load == "no active plan
      // for this learner's grade".
      frameworkAnnualPlanWeekOutcomeIdListByCoterieTypeMap: {},
      announcementDTOList: [],
      announcementListLoading: true,
      numberOfUnreadAnnouncements: 0,
      contestDTOList: [],
      contestListLoading: true,

      personalStatus: {
        remainingPoints: 0,
        totalPoints: 0,
        seasonRank: 0,
        branchRank: 0,
      },
      weeklyStreak: {
        activeDaysOfWeek: [],
        currentStreakDays: 0,
        todayActive: false,
        weeklyActivityCount: 0,
      },
      nextAchievementProgress: null,
      summedActionCounts: [],
      learnerGoalProgressList: [],
      myWrongActivityDTOList: [],
      myWrongActivityListLoading: true,
      activityReviewDTOList: [],
      activityReviewListLoading: true,
      thisWeekRankingDTOList: [],
      thisWeekRankingListLoading: true,
      allTimeRankingDTOList: [],
      allTimeRankingListLoading: true,
      // Bounded-ranking companions from the gamification bootstrap (rankingLimit): the learner's
      // own row (with server-computed branchRank) + per-branch aggregate standings. Null/empty
      // when the backend hasn't been asked for a bounded ranking.
      selfThisWeekRanking: null,
      selfAllTimeRanking: null,
      thisWeekBranchRankingDTOList: [],
      allTimeBranchRankingDTOList: [],
      leagueItemProgressDTOList: [],
      leagueItemProgressListLoading: true,
      taskItemProgressDTOList: [],
      taskItemProgressListLoading: true,
      earnedAchievementDTOList: [],
      earnedAchievementListLoading: true,
      newEarnedAchievementDTO: null,
      newEarnedAchievementDTOList: [],
      mascotMode: "INFORMATION",
      showMascot: false,
      showMascotText: false,
      mascotText: null,
      mascotSubText: null,
      mascotSubTextList: [],
      mascotPrimaryButtonText: null,
      mascotPrimaryButtonOnClick: () => {},
      mascotChatActivityId: null,
      mascotChatActivityTemplateId: null,
      mascotChatActivityTemplateType: null,
      mascotChatWelcomeText: null,
      mascotChatList: [],
      mascotSendMessageOnClick: () => {},
      mascotChatLoading: false,
      mascotChatCanConvertAudio: false,
      mascotHasVoiceItem: false,
      mascotChatCanDraw: true,
      mascotInventoryItemDTOList: [],
      mascotInventoryItemListLoading: false,
      mascotChatVoiceType: "EKREM_VOICE",
      mascotChatActType: "DEFAULT_ACT",
      mascotPosition: "RIGHT",
      mascotChatErrorText: null,
      mascotChatContinueText: null,
      mascotChatContinueOnClick: () => {},
      mascotChatActionList: [],
      subjectDescriptionDTOList: [],
      subjectDescriptionListLoading: false,
      subjectDescriptionCompletionDTOList: [],
      subjectDescriptionCompletionListLoading: false,
      marketDTO: null,
      marketItemPurchaseDTOList: [],
      marketItemPurchaseListLoading: false,
      webSocketStompClient: null,
      webSocketConnectionStatus: "disconnected",
      // Raised ONLY by useWebSocket's idle reaper, so the modal it drives can't be confused with an
      // ordinary connection drop. See SessionInactiveModal.
      sessionInactiveNoticeOpen: false,
      // Bumped to force LearnerApp + App data-fetch effects to re-run after a learner action
      // (e.g. completing a personalized assignment) so /learning-path reflects fresh state.
      learnerDataRefreshCounter: 0,
    };
    this.handleStateLogin = this.handleStateLogin.bind(this);
    this.handleStateLogout = this.handleStateLogout.bind(this);
    this.handleStateChooseProfile = this.handleStateChooseProfile.bind(this);
    this.handleStateChangeProfile = this.handleStateChangeProfile.bind(this);
    this.handleUpdateAndSetActivityReviewList =
      this.handleUpdateAndSetActivityReviewList.bind(this);
    this.setUserInformation = this.setUserInformation.bind(this);
    this.setLanguage = this.setLanguage.bind(this);
    this.setCategoryDTOList = this.setCategoryDTOList.bind(this);
    this.setCategoryDTOListAsTree = this.setCategoryDTOListAsTree.bind(this);
    this.setSkillDTOList = this.setSkillDTOList.bind(this);
    this.setSkillDTOListAsTree = this.setSkillDTOListAsTree.bind(this);
    this.setDistinctVersionCodeLevelList =
      this.setDistinctVersionCodeLevelList.bind(this);
    this.setUserProfileDTOList = this.setUserProfileDTOList.bind(this);
    this.setUserProfile = this.setUserProfile.bind(this);
    this.setUserProfileSeason = this.setUserProfileSeason.bind(this);
    this.setUserProfileGrade = this.setUserProfileGrade.bind(this);
    this.setUserProfileBranch = this.setUserProfileBranch.bind(this);
    this.setUserProfileInstitution = this.setUserProfileInstitution.bind(this);
    this.setBranchAssignmentDTOList =
      this.setBranchAssignmentDTOList.bind(this);
    this.setSupportAssignmentDTOList = this.setSupportAssignmentDTOList.bind(this);
    this.setSupportAssignmentListLoading = this.setSupportAssignmentListLoading.bind(this);
    this.setBranchAssignmentListLoading =
      this.setBranchAssignmentListLoading.bind(this);
    this.setStandardExamApplicationDTOList =
      this.setStandardExamApplicationDTOList.bind(this);
    this.setStandardExamApplicationListLoading =
      this.setStandardExamApplicationListLoading.bind(this);
    this.setActiveContentList = this.setActiveContentList.bind(this);
    this.setPassiveContentList = this.setPassiveContentList.bind(this);
    this.setPreviousSupportAssignmentList =
      this.setPreviousSupportAssignmentList.bind(this);
    this.setPassiveContentListLoaded =
      this.setPassiveContentListLoaded.bind(this);
    this.setPreviousSupportAssignmentListLoaded =
      this.setPreviousSupportAssignmentListLoaded.bind(this);
    this.setActivePersonalizedAssignmentList =
      this.setActivePersonalizedAssignmentList.bind(this);
    this.setPassivePersonalizedAssignmentList =
      this.setPassivePersonalizedAssignmentList.bind(this);
    this.setPersonalizedAssignmentDTOList =
      this.setPersonalizedAssignmentDTOList.bind(this);
    this.setPersonalizedAssignmentListLoading =
      this.setPersonalizedAssignmentListLoading.bind(this);
    this.setBranchAssignmentPerformanceLoading =
      this.setBranchAssignmentPerformanceLoading.bind(this);
    this.setBranchAssignmentPerformance =
      this.setBranchAssignmentPerformance.bind(this);
    this.setLearnerHandwrittenExamPerformanceLoading =
      this.setLearnerHandwrittenExamPerformanceLoading.bind(this);
    this.setLearnerHandwrittenExamPerformance =
      this.setLearnerHandwrittenExamPerformance.bind(this);
    this.setLearnerMockExamPerformanceLoading =
      this.setLearnerMockExamPerformanceLoading.bind(this);
    this.setLearnerMockExamPerformance =
      this.setLearnerMockExamPerformance.bind(this);
    this.setLearnerSupportAssignmentPerformanceLoading =
      this.setLearnerSupportAssignmentPerformanceLoading.bind(this);
    this.setLearnerSupportAssignmentPerformance =
      this.setLearnerSupportAssignmentPerformance.bind(this);
    this.setPersonalizedAssignmentPerformanceLoading =
      this.setPersonalizedAssignmentPerformanceLoading.bind(this);
    this.setPersonalizedAssignmentPerformance =
      this.setPersonalizedAssignmentPerformance.bind(this);
    this.setBranchAssignmentReportDetailListLoading =
      this.setBranchAssignmentReportDetailListLoading.bind(this);
    this.setBranchAssignmentReportDetailDTOList =
      this.setBranchAssignmentReportDetailDTOList.bind(this);
    this.setLearnerCompletionOutcomeReportMap =
      this.setLearnerCompletionOutcomeReportMap.bind(this);
    this.setLearnerHandwrittenOutcomeReportMap =
      this.setLearnerHandwrittenOutcomeReportMap.bind(this);
    this.setLearnerMockExamOutcomeReportMap =
      this.setLearnerMockExamOutcomeReportMap.bind(this);
    this.setLearnerSupportAssignmentOutcomeReportMap =
      this.setLearnerSupportAssignmentOutcomeReportMap.bind(this);
    this.setReportCategoryDTOList = this.setReportCategoryDTOList.bind(this);
    this.setReportDTOList = this.setReportDTOList.bind(this);
    this.setIndividualModelDTOList = this.setIndividualModelDTOList.bind(this);
    this.setIndividualModelListLoading =
      this.setIndividualModelListLoading.bind(this);
    this.setAvailableFrameworkAnnualPlanList =
      this.setAvailableFrameworkAnnualPlanList.bind(this);
    this.setAvailableFrameworkAnnualPlanListLoading =
      this.setAvailableFrameworkAnnualPlanListLoading.bind(this);
    this.setFrameworkAnnualPlanWeekOutcomeIdListByCoterieTypeMap =
      this.setFrameworkAnnualPlanWeekOutcomeIdListByCoterieTypeMap.bind(this);
    this.setFrameworkAnnualPlanMap = this.setFrameworkAnnualPlanMap.bind(this);
    this.setFrameworkAnnualPlanFileMap =
      this.setFrameworkAnnualPlanFileMap.bind(this);
    this.setAnnouncementListLoading =
      this.setAnnouncementListLoading.bind(this);
    this.setAnnouncementDTOList = this.setAnnouncementDTOList.bind(this);
    this.setAnnouncementListLoading =
      this.setAnnouncementListLoading.bind(this);
    this.setNumberOfUnreadAnnouncements =
      this.setNumberOfUnreadAnnouncements.bind(this);
    this.setContestDTOList = this.setContestDTOList.bind(this);
    this.setContestListLoading = this.setContestListLoading.bind(this);

    this.setPersonalStatus = this.setPersonalStatus.bind(this);
    this.setWeeklyStreak = this.setWeeklyStreak.bind(this);
    this.setMyWrongActivityListLoading =
      this.setMyWrongActivityListLoading.bind(this);
    this.setMyWrongActivityDTOList = this.setMyWrongActivityDTOList.bind(this);
    this.setActivityReviewListLoading =
      this.setActivityReviewListLoading.bind(this);
    this.setActivityReviewDTOList = this.setActivityReviewDTOList.bind(this);
    this.setThisWeekRankingDTOList = this.setThisWeekRankingDTOList.bind(this);
    this.setThisWeekRankingListLoading =
      this.setThisWeekRankingListLoading.bind(this);
    this.setAllTimeRankingDTOList = this.setAllTimeRankingDTOList.bind(this);
    this.setAllTimeRankingListLoading =
      this.setAllTimeRankingListLoading.bind(this);
    this.setSelfThisWeekRanking = this.setSelfThisWeekRanking.bind(this);
    this.setSelfAllTimeRanking = this.setSelfAllTimeRanking.bind(this);
    this.setThisWeekBranchRankingDTOList =
      this.setThisWeekBranchRankingDTOList.bind(this);
    this.setAllTimeBranchRankingDTOList =
      this.setAllTimeBranchRankingDTOList.bind(this);
    this.setLeagueItemProgressDTOList =
      this.setLeagueItemProgressDTOList.bind(this);
    this.setLeagueItemProgressListLoading =
      this.setLeagueItemProgressListLoading.bind(this);
    this.setTaskItemProgressDTOList =
      this.setTaskItemProgressDTOList.bind(this);
    this.setTaskItemProgressListLoading =
      this.setTaskItemProgressListLoading.bind(this);
    this.setEarnedAchievementDTOList = this.setEarnedAchievementDTOList.bind(this);
    this.setEarnedAchievementListLoading = this.setEarnedAchievementListLoading.bind(this);
    this.setNewEarnedAchievementDTO = this.setNewEarnedAchievementDTO.bind(this);
    this.setNewEarnedAchievementDTOList = this.setNewEarnedAchievementDTOList.bind(this);
    this.resetMascotParams = this.resetMascotParams.bind(this);
    this.setMascotMode = this.setMascotMode.bind(this);
    this.setShowMascot = this.setShowMascot.bind(this);
    this.setShowMascotText = this.setShowMascotText.bind(this);
    this.setMascotText = this.setMascotText.bind(this);
    this.setMascotSubText = this.setMascotSubText.bind(this);
    this.setMascotSubTextList = this.setMascotSubTextList.bind(this);
    this.setMascotPrimaryButtonText =
      this.setMascotPrimaryButtonText.bind(this);
    this.setMascotPrimaryButtonOnClick =
      this.setMascotPrimaryButtonOnClick.bind(this);
    this.setMascotChatActivityId = this.setMascotChatActivityId.bind(this);
    this.setMascotChatActivityTemplateId =
      this.setMascotChatActivityTemplateId.bind(this);
    this.setMascotChatActivityTemplateType =
      this.setMascotChatActivityTemplateType.bind(this);
    this.setMascotChatWelcomeText = this.setMascotChatWelcomeText.bind(this);
    this.setMascotChatList = this.setMascotChatList.bind(this);
    this.setMascotSendMessageOnClick =
      this.setMascotSendMessageOnClick.bind(this);
    this.setMascotChatLoading = this.setMascotChatLoading.bind(this);
    this.setMascotChatCanConvertAudio =
      this.setMascotChatCanConvertAudio.bind(this);
    this.setMascotHasVoiceItem = this.setMascotHasVoiceItem.bind(this);
    this.setMascotChatCanDraw = this.setMascotChatCanDraw.bind(this);
    this.setMascotChatVoiceType = this.setMascotChatVoiceType.bind(this);
    this.setMascotChatActType = this.setMascotChatActType.bind(this);
    this.setMascotInventoryItemDTOList =
      this.setMascotInventoryItemDTOList.bind(this);
    this.setMascotInventoryItemListLoading =
      this.setMascotInventoryItemListLoading.bind(this);
    this.setMascotPosition = this.setMascotPosition.bind(this);
    this.setMascotChatErrorText = this.setMascotChatErrorText.bind(this);
    this.setMascotChatContinueText = this.setMascotChatContinueText.bind(this);
    this.setMascotChatContinueOnClick =
      this.setMascotChatContinueOnClick.bind(this);
    this.setMascotChatActionList = this.setMascotChatActionList.bind(this);
    this.setSubjectDescriptionDTOList =
      this.setSubjectDescriptionDTOList.bind(this);
    this.setSubjectDescriptionListLoading =
      this.setSubjectDescriptionListLoading.bind(this);
    this.setSubjectDescriptionCompletionDTOList =
      this.setSubjectDescriptionCompletionDTOList.bind(this);
    this.setSubjectDescriptionCompletionListLoading =
      this.setSubjectDescriptionCompletionListLoading.bind(this);
    this.setBranchAssignmentCategoryScoreList =
      this.setBranchAssignmentCategoryScoreList.bind(this);
    this.triggerLearnerDataRefresh = this.triggerLearnerDataRefresh.bind(this);
    this.setMarketDTO = this.setMarketDTO.bind(this);
    this.setMarketItemPurchaseDTOList =
      this.setMarketItemPurchaseDTOList.bind(this);
    this.setMarketItemPurchaseListLoading =
      this.setMarketItemPurchaseListLoading.bind(this);
    this.setWebSocketStompClient = this.setWebSocketStompClient.bind(this);
    this.setWebSocketConnectionStatus =
      this.setWebSocketConnectionStatus.bind(this);
  }

  handleStateLogin(
    accessToken,
    refreshToken,
    userInformation,
    userProfileDTOList,
  ) {
    if (accessToken) {
      removeAxiosInterceptor();
      // setAxiosInterceptor takes 0 args and reads accessToken from localStorage
      // itself; the original JS call passed accessToken redundantly.
      setAxiosInterceptor();
      localStorage.setItem("accessToken", accessToken);
    }
    if (userInformation) {
      localStorage.setItem("userId", userInformation.id);
      this.setUserInformation(userInformation);
    }
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }
    if (userProfileDTOList) {
      this.setUserProfileDTOList(userProfileDTOList);
    }
  }

  handleStateLogout() {
    removeAxiosInterceptor();
    this.setUserInformation(null);
    this.handleStateChangeProfile();
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userId");
    // An impersonated learner pressing "Çıkış" must not leave the banner floating over a logged-out app —
    // it keys off storage, not off a live session, so it would otherwise survive its own session.
    clearImpersonationState();
    // The idle notice is a property of the session that just ended. Leaving it raised means a
    // same-tab logout -> login re-shows a blocking modal about a socket the new session never had.
    this.setState({ sessionInactiveNoticeOpen: false });
    // Same argument for the AI-limit band: it carries THIS learner's budget and reset time, so it must
    // not survive onto the login screen — where its timer would also still be able to fire a credit
    // read after the interceptors above were ejected.
    dispatchAiRefusalReset();
    localStorage.setItem("appState", "LOGOUT");
  }

  handleStateChooseProfile(userProfileDTO, seasonDTO, gradeDTO, branchDTO) {
    this.setUserProfileSeason(seasonDTO);
    this.setUserProfileGrade(gradeDTO);
    this.setUserProfileBranch(branchDTO);
    // The learner's institution (with its eligibleCoterieTypeList) rides along on the
    // season DTO. Surface it directly so coterie pickers can bound by institution.
    this.setUserProfileInstitution(seasonDTO?.institutionDTO || null);
    this.setUserProfile(userProfileDTO);
    localStorage.setItem("userProfileId", userProfileDTO.id);
    if (seasonDTO) localStorage.setItem("seasonId", seasonDTO.id);
    if (gradeDTO) {
      let foundLanguage = localStorage.getItem("language");
      if (foundLanguage) {
        this.setLanguage(foundLanguage);
      } else {
        if (gradeDTO.curriculumType === "MEB") {
          foundLanguage = "TR";
        } else {
          foundLanguage = "EN";
        }
      }
      this.setLanguage(foundLanguage);
      localStorage.setItem("language", foundLanguage);
      localStorage.setItem("gradeId", gradeDTO.id);
    }
    if (branchDTO) localStorage.setItem("branchId", branchDTO.id);
  }

  handleStateChangeProfile() {
    this.setUserProfile(null);
    this.setUserProfileSeason(null);
    this.setUserProfileGrade(null);
    this.setUserProfileBranch(null);
    this.setUserProfileInstitution(null);
    // Invalidate the /practise previous-list cache — it's identity-scoped, so a
    // profile switch (or logout) must force a re-fetch for the next profile.
    this.setPassiveContentList([]);
    this.setPreviousSupportAssignmentList([]);
    this.setPassiveContentListLoaded(false);
    this.setPreviousSupportAssignmentListLoaded(false);
    localStorage.removeItem("userProfileId");
    localStorage.removeItem("seasonId");
    localStorage.removeItem("gradeId");
    localStorage.removeItem("branchId");
    // RETIRED selection keys — swept, no longer written by anything. Every lesson picker, tab strip
    // and filter row keeps its selection in the URL now (see UrlSelectionUtilization), which is what
    // this list was working around: localStorage is scoped per ORIGIN, not per learner, so a value
    // left here was carried into the NEXT profile signed in on the same browser. Clearing it on
    // profile switch was the patch; the URL owning the selection is the fix.
    //
    // Kept as a one-time sweep so the keys already sitting in existing learners' browsers do not
    // linger. Safe to delete once those have aged out.
    [
      "practiseHomeworkCoterieType",
      "practiseHomeworkContentType",
      "practiseMyWrongActivitiesCoterieType",
      "practiseMyWrongActivitiesMainCategoryCode",
      "practiseMyWrongActivitiesSourceType",
      "practiseMyWrongActivitiesActivityTemplateTypes",
      "practiseMyWrongActivitiesPageNumber",
      "practiseNowState",
      "practiseCoterieType",
      "reportsCoterieType",
      "learningPathCoterieType",
      "frameworkAnnualPlanCoterieType",
      "frameworkAnnualPlanVersionCode",
      "subjectDescriptionsCoterieType",
      "subjectDescriptionsVersionCode",
    ].forEach((retiredSelectionKey) =>
      localStorage.removeItem(retiredSelectionKey),
    );
  }

  resetMascotParams() {
    this.setMascotMode("INFORMATION");
    this.setShowMascot(false);
    this.setShowMascotText(false);
    this.setMascotText(null);
    this.setMascotSubText(null);
    this.setMascotSubTextList([]);
    this.setMascotPrimaryButtonText(null);
    this.setMascotPrimaryButtonOnClick(() => {});
    this.setMascotChatList([]);
    this.setMascotChatLoading(false);
    this.setMascotSendMessageOnClick(() => {});
    this.setMascotChatErrorText(null);
    this.setMascotChatContinueText(null);
    this.setMascotChatContinueOnClick(null);
    this.setMascotChatActionList([]);
    this.setMascotChatActivityId(null);
    this.setMascotChatActivityTemplateId(null);
    this.setMascotChatActivityTemplateType(null);
  }

  async handleUpdateAndSetActivityReviewList(
    sourceType,
    sourceId,
    activityId,
    activityTemplateId,
    activityTemplateType,
  ) {
    const { data, err } =
      await createActivityReviewBySourceTypeAndSourceIdAndActivityIdAndActivityTemplateId(
        {
          userId: this.state.userInformation.id,
          userProfileId: this.state.userProfile.id,
          sourceType,
          sourceId,
          isStarred: false,
          activityTemplate: {
            id: activityTemplateId,
            type: activityTemplateType,
          },
          activity: {
            id: activityId,
          },
        },
      );
    if (err) {
      return;
    }
    this.state.activityReviewDTOList.push(data);
    this.setActivityReviewDTOList(this.state.activityReviewDTOList);
  }

  setUserInformation(userInformation) {
    this.setState({ userInformation });
  }

  setLanguage(language) {
    this.setState({ language });
  }

  setCategoryDTOList(categoryDTOList) {
    this.setState({ categoryDTOList });
  }

  setCategoryDTOListAsTree(categoryDTOListAsTree) {
    this.setState({ categoryDTOListAsTree });
  }

  setSkillDTOList(skillDTOList) {
    this.setState({ skillDTOList });
  }

  setSkillDTOListAsTree(skillDTOListAsTree) {
    this.setState({ skillDTOListAsTree });
  }

  setDistinctVersionCodeLevelList(distinctVersionCodeLevelList) {
    this.setState({ distinctVersionCodeLevelList });
  }

  setUserProfileDTOList(userProfileDTOList) {
    this.setState({ userProfileDTOList });
  }

  setUserProfile(userProfile) {
    this.setState({ userProfile });
  }

  setUserProfileSeason(userProfileSeason) {
    this.setState({ userProfileSeason });
  }

  setUserProfileGrade(userProfileGrade) {
    this.setState({ userProfileGrade });
  }

  setUserProfileBranch(userProfileBranch) {
    this.setState({ userProfileBranch });
  }

  setUserProfileInstitution(userProfileInstitution) {
    this.setState({ userProfileInstitution });
  }

  setBranchAssignmentDTOList(branchAssignmentDTOList) {
    this.setState({ branchAssignmentDTOList });
  }
  setSupportAssignmentDTOList(supportAssignmentDTOList) {
    this.setState({ supportAssignmentDTOList });
  }
  setSupportAssignmentListLoading(supportAssignmentListLoading) {
    this.setState({ supportAssignmentListLoading });
  }

  setBranchAssignmentListLoading(branchAssignmentListLoading) {
    this.setState({ branchAssignmentListLoading });
  }

  setStandardExamApplicationDTOList(
    standardExamApplicationDTOList,
  ) {
    this.setState({ standardExamApplicationDTOList });
  }

  setStandardExamApplicationListLoading(
    standardExamApplicationListLoading,
  ) {
    this.setState({ standardExamApplicationListLoading });
  }

  setActiveContentList(activeContentList) {
    this.setState({ activeContentList });
  }

  setPassiveContentList(passiveContentList) {
    this.setState({ passiveContentList });
  }

  setPreviousSupportAssignmentList(previousSupportAssignmentList) {
    this.setState({ previousSupportAssignmentList });
  }

  setPassiveContentListLoaded(passiveContentListLoaded) {
    this.setState({ passiveContentListLoaded });
  }

  setPreviousSupportAssignmentListLoaded(previousSupportAssignmentListLoaded) {
    this.setState({ previousSupportAssignmentListLoaded });
  }

  setPersonalizedAssignmentDTOList(personalizedAssignmentDTOList) {
    this.setState({ personalizedAssignmentDTOList });
  }

  setPersonalizedAssignmentListLoading(personalizedAssignmentListLoading) {
    this.setState({ personalizedAssignmentListLoading });
  }

  setActivePersonalizedAssignmentList(activePersonalizedAssignmentList) {
    this.setState({ activePersonalizedAssignmentList });
  }

  setPassivePersonalizedAssignmentList(passivePersonalizedAssignmentList) {
    this.setState({ passivePersonalizedAssignmentList });
  }

  setBranchAssignmentPerformanceLoading(
    branchAssignmentPerformanceLoading,
  ) {
    this.setState({ branchAssignmentPerformanceLoading });
  }

  setBranchAssignmentPerformance(branchAssignmentPerformance) {
    this.setState({ branchAssignmentPerformance });
  }

  setLearnerMockExamPerformanceLoading(learnerMockExamPerformanceLoading) {
    this.setState({ learnerMockExamPerformanceLoading });
  }

  setLearnerMockExamPerformance(learnerMockExamPerformance) {
    this.setState({ learnerMockExamPerformance });
  }

  setLearnerHandwrittenExamPerformanceLoading(learnerHandwrittenExamPerformanceLoading) {
    this.setState({ learnerHandwrittenExamPerformanceLoading });
  }

  setLearnerHandwrittenExamPerformance(learnerHandwrittenExamPerformance) {
    this.setState({ learnerHandwrittenExamPerformance });
  }

  setLearnerSupportAssignmentPerformanceLoading(learnerSupportAssignmentPerformanceLoading) {
    this.setState({ learnerSupportAssignmentPerformanceLoading });
  }

  setPersonalizedAssignmentPerformanceLoading(
    personalizedAssignmentPerformanceLoading,
  ) {
    this.setState({ personalizedAssignmentPerformanceLoading });
  }

  setPersonalizedAssignmentPerformance(personalizedAssignmentPerformance) {
    this.setState({ personalizedAssignmentPerformance });
  }

  setLearnerSupportAssignmentPerformance(learnerSupportAssignmentPerformance) {
    this.setState({ learnerSupportAssignmentPerformance });
  }

  setBranchAssignmentReportDetailListLoading(
    branchAssignmentReportDetailListLoading,
  ) {
    this.setState({
      branchAssignmentReportDetailListLoading,
    });
  }

  setBranchAssignmentReportDetailDTOList(
    branchAssignmentReportDetailDTOList,
  ) {
    this.setState({ branchAssignmentReportDetailDTOList });
  }

  setLearnerCompletionOutcomeReportMap(learnerCompletionOutcomeReportMap) {
    this.setState({ learnerCompletionOutcomeReportMap });
  }

  setLearnerMockExamOutcomeReportMap(learnerMockExamOutcomeReportMap) {
    this.setState({ learnerMockExamOutcomeReportMap });
  }

  setLearnerHandwrittenOutcomeReportMap(learnerHandwrittenOutcomeReportMap) {
    this.setState({ learnerHandwrittenOutcomeReportMap });
  }

  setLearnerSupportAssignmentOutcomeReportMap(learnerSupportAssignmentOutcomeReportMap) {
    this.setState({ learnerSupportAssignmentOutcomeReportMap });
  }

  setReportCategoryDTOList(reportCategoryDTOList) {
    this.setState({ reportCategoryDTOList });
  }

  setBranchAssignmentCategoryScoreList(
    branchAssignmentCategoryScoreList,
  ) {
    this.setState({ branchAssignmentCategoryScoreList });
  }

  setReportDTOList(reportDTOList) {
    this.setState({ reportDTOList });
  }


  setIndividualModelDTOList(individualModelDTOList) {
    this.setState({ individualModelDTOList });
  }

  setIndividualModelListLoading(individualModelListLoading) {
    this.setState({ individualModelListLoading });
  }

  setNumberOfUnreadAnnouncements(numberOfUnreadAnnouncements) {
    this.setState({ numberOfUnreadAnnouncements });
  }

  setAvailableFrameworkAnnualPlanList(availableFrameworkAnnualPlanList) {
    this.setState({ availableFrameworkAnnualPlanList });
  }

  setAvailableFrameworkAnnualPlanListLoading(availablePlanListLoading) {
    this.setState({ availablePlanListLoading });
  }

  setFrameworkAnnualPlanWeekOutcomeIdListByCoterieTypeMap(
    frameworkAnnualPlanWeekOutcomeIdListByCoterieTypeMap,
  ) {
    this.setState({ frameworkAnnualPlanWeekOutcomeIdListByCoterieTypeMap });
  }

  setFrameworkAnnualPlanMap(frameworkAnnualPlanMap) {
    this.setState({ frameworkAnnualPlanMap });
  }

  setFrameworkAnnualPlanFileMap(frameworkAnnualPlanFileMap) {
    this.setState({ frameworkAnnualPlanFileMap });
  }

  setAnnouncementDTOList(announcementDTOList) {
    this.setState({ announcementDTOList });
  }

  setAnnouncementListLoading(announcementListLoading) {
    this.setState({ announcementListLoading });
  }

  setContestDTOList(contestDTOList) {
    this.setState({ contestDTOList });
  }

  setContestListLoading(contestListLoading) {
    this.setState({ contestListLoading });
  }


  setPersonalStatus(personalStatus) {
    this.setState({ personalStatus });
  }

  setWeeklyStreak(weeklyStreak) {
    this.setState({ weeklyStreak });
  }

  setNextAchievementProgress = (nextAchievementProgress) => {
    this.setState({ nextAchievementProgress });
  };

  setSummedActionCounts = (summedActionCounts) => {
    this.setState({ summedActionCounts });
  };

  setLearnerGoalProgressList = (learnerGoalProgressList) => {
    this.setState({ learnerGoalProgressList });
  };

  setMyWrongActivityDTOList(myWrongActivityDTOList) {
    this.setState({ myWrongActivityDTOList });
  }

  setMyWrongActivityListLoading(myWrongActivityListLoading) {
    this.setState({ myWrongActivityListLoading });
  }

  setActivityReviewDTOList(activityReviewDTOList) {
    this.setState({ activityReviewDTOList });
  }

  setActivityReviewListLoading(activityReviewListLoading) {
    this.setState({ activityReviewListLoading });
  }

  setThisWeekRankingDTOList(thisWeekRankingDTOList) {
    this.setState({ thisWeekRankingDTOList });
  }

  setThisWeekRankingListLoading(thisWeekRankingListLoading) {
    this.setState({ thisWeekRankingListLoading });
  }

  setAllTimeRankingDTOList(allTimeRankingDTOList) {
    this.setState({ allTimeRankingDTOList });
  }

  setAllTimeRankingListLoading(allTimeRankingListLoading) {
    this.setState({ allTimeRankingListLoading });
  }

  setSelfThisWeekRanking(selfThisWeekRanking) {
    this.setState({ selfThisWeekRanking });
  }

  setSelfAllTimeRanking(selfAllTimeRanking) {
    this.setState({ selfAllTimeRanking });
  }

  setThisWeekBranchRankingDTOList(thisWeekBranchRankingDTOList) {
    this.setState({ thisWeekBranchRankingDTOList });
  }

  setAllTimeBranchRankingDTOList(allTimeBranchRankingDTOList) {
    this.setState({ allTimeBranchRankingDTOList });
  }

  setLeagueItemProgressDTOList(leagueItemProgressDTOList) {
    this.setState({ leagueItemProgressDTOList });
  }

  setLeagueItemProgressListLoading(leagueItemProgressListLoading) {
    this.setState({ leagueItemProgressListLoading });
  }

  setTaskItemProgressDTOList(taskItemProgressDTOList) {
    this.setState({ taskItemProgressDTOList });
  }

  setTaskItemProgressListLoading(
    taskItemProgressListLoading,
  ) {
    this.setState({ taskItemProgressListLoading });
  }

  setEarnedAchievementDTOList(earnedAchievementDTOList) {
    this.setState({ earnedAchievementDTOList });
  }

  setEarnedAchievementListLoading(earnedAchievementListLoading) {
    this.setState({ earnedAchievementListLoading });
  }

  setNewEarnedAchievementDTO(newEarnedAchievementDTO) {
    this.setState({ newEarnedAchievementDTO });
  }

  setNewEarnedAchievementDTOList(next) {
    // Accept a functional updater (like React's setState) so callers can append
    // race-free against the latest value — achievements arrive one-by-one over the
    // websocket and a single action can award several back-to-back.
    this.setState((state) => ({
      newEarnedAchievementDTOList:
        typeof next === "function" ? next(state.newEarnedAchievementDTOList) : next,
    }));
  }

  setMascotMode(mascotMode) {
    this.setState({ mascotMode });
  }

  setShowMascot(showMascot) {
    this.setState({ showMascot });
  }

  setShowMascotText(showMascotText) {
    this.setState({ showMascotText });
  }

  setMascotText(mascotText) {
    this.setState({ mascotText });
  }

  setMascotSubText(mascotSubText) {
    this.setState({ mascotSubText });
  }

  setMascotSubTextList(mascotSubTextList) {
    this.setState({ mascotSubTextList });
  }

  setMascotPrimaryButtonText(mascotPrimaryButtonText) {
    this.setState({ mascotPrimaryButtonText });
  }

  setMascotPrimaryButtonOnClick(mascotPrimaryButtonOnClick) {
    this.setState({ mascotPrimaryButtonOnClick });
  }

  setMascotChatActivityId(mascotChatActivityId) {
    this.setState({ mascotChatActivityId });
  }

  setMascotChatActivityTemplateId(mascotChatActivityTemplateId) {
    this.setState({ mascotChatActivityTemplateId });
  }

  setMascotChatActivityTemplateType(mascotChatActivityTemplateType) {
    this.setState({ mascotChatActivityTemplateType });
  }

  setMascotChatWelcomeText(mascotChatWelcomeText) {
    this.setState({ mascotChatWelcomeText });
  }

  setMascotChatList(mascotChatList) {
    this.setState({ mascotChatList });
  }

  setMascotSendMessageOnClick = (mascotSendMessageOnClick) => {
    this.setState({ mascotSendMessageOnClick });
  };

  setMascotChatLoading = (mascotChatLoading) => {
    this.setState({ mascotChatLoading });
  };

  setMascotChatCanConvertAudio = (mascotChatCanConvertAudio) => {
    this.setState({ mascotChatCanConvertAudio });
  };

  setMascotHasVoiceItem = (mascotHasVoiceItem) => {
    this.setState({ mascotHasVoiceItem });
  };

  setMascotChatCanDraw = (mascotChatCanDraw) => {
    this.setState({ mascotChatCanDraw });
  };

  setMascotChatVoiceType = (mascotChatVoiceType) => {
    this.setState({ mascotChatVoiceType });
  };

  setMascotChatActType = (mascotChatActType) => {
    this.setState({ mascotChatActType });
  };

  setMascotInventoryItemDTOList = (mascotInventoryItemDTOList) => {
    this.setState({ mascotInventoryItemDTOList });
  };

  setMascotInventoryItemListLoading = (mascotInventoryItemListLoading) => {
    this.setState({ mascotInventoryItemListLoading });
  };

  setMascotPosition = (mascotPosition) => {
    this.setState({ mascotPosition });
  };

  setMascotChatErrorText = (mascotChatErrorText) => {
    this.setState({ mascotChatErrorText });
  };

  setMascotChatContinueText = (mascotChatContinueText) => {
    this.setState({ mascotChatContinueText });
  };

  setMascotChatContinueOnClick = (mascotChatContinueOnClick) => {
    this.setState({ mascotChatContinueOnClick });
  };

  setMascotChatActionList = (mascotChatActionList) => {
    this.setState({ mascotChatActionList: mascotChatActionList || [] });
  };

  setSubjectDescriptionDTOList = (subjectDescriptionDTOList) => {
    this.setState({ subjectDescriptionDTOList });
  };

  triggerLearnerDataRefresh() {
    this.setState((prev) => ({
      learnerDataRefreshCounter: prev.learnerDataRefreshCounter + 1,
    }));
  }

  setSubjectDescriptionListLoading = (subjectDescriptionListLoading) => {
    this.setState({ subjectDescriptionListLoading });
  };

  setSubjectDescriptionCompletionDTOList = (
    subjectDescriptionCompletionDTOList,
  ) => {
    this.setState({ subjectDescriptionCompletionDTOList });
  };

  setSubjectDescriptionCompletionListLoading = (
    subjectDescriptionCompletionListLoading,
  ) => {
    this.setState({ subjectDescriptionCompletionListLoading });
  };

  setMarketDTO = (marketDTO) => {
    this.setState({ marketDTO });
  };
  setMarketItemPurchaseDTOList = (marketItemPurchaseDTOList) => {
    this.setState({ marketItemPurchaseDTOList });
  };

  setMarketItemPurchaseListLoading = (marketItemPurchaseListLoading) => {
    this.setState({ marketItemPurchaseListLoading });
  };

  setWebSocketStompClient = (webSocketStompClient) => {
    this.setState({ webSocketStompClient });
  };

  setWebSocketConnectionStatus = (webSocketConnectionStatus) => {
    this.setState({ webSocketConnectionStatus });
  };

  setSessionInactiveNoticeOpen = (sessionInactiveNoticeOpen) => {
    this.setState({ sessionInactiveNoticeOpen });
  };

  render() {
    return (
      <UserContext.Provider
        value={{
          ...this.state,
          handleStateLogin: this.handleStateLogin,
          handleStateLogout: this.handleStateLogout,
          handleStateChooseProfile: this.handleStateChooseProfile,
          handleStateChangeProfile: this.handleStateChangeProfile,
          handleUpdateAndSetActivityReviewList:
            this.handleUpdateAndSetActivityReviewList,
          setUserInformation: this.setUserInformation,
          setLanguage: this.setLanguage,
          setUserProfileDTOList: this.setUserProfileDTOList,
          setCategoryDTOList: this.setCategoryDTOList,
          setCategoryDTOListAsTree: this.setCategoryDTOListAsTree,
          setSkillDTOList: this.setSkillDTOList,
          setSkillDTOListAsTree: this.setSkillDTOListAsTree,
          setDistinctVersionCodeLevelList: this.setDistinctVersionCodeLevelList,
          setUserProfile: this.setUserProfile,
          setUserProfileSeason: this.setUserProfileSeason,
          setUserProfileGrade: this.setUserProfileGrade,
          setUserProfileBranch: this.setUserProfileBranch,
          setUserProfileInstitution: this.setUserProfileInstitution,
          setBranchAssignmentDTOList: this.setBranchAssignmentDTOList,
          setBranchAssignmentListLoading:
            this.setBranchAssignmentListLoading,
          setSupportAssignmentDTOList: this.setSupportAssignmentDTOList,
          setSupportAssignmentListLoading: this.setSupportAssignmentListLoading,
          setStandardExamApplicationDTOList:
            this.setStandardExamApplicationDTOList,
          setStandardExamApplicationListLoading:
            this.setStandardExamApplicationListLoading,
          setActiveContentList: this.setActiveContentList,
          setPassiveContentList: this.setPassiveContentList,
          setPreviousSupportAssignmentList: this.setPreviousSupportAssignmentList,
          setPassiveContentListLoaded: this.setPassiveContentListLoaded,
          setPreviousSupportAssignmentListLoaded:
            this.setPreviousSupportAssignmentListLoaded,
          setPersonalizedAssignmentDTOList:
            this.setPersonalizedAssignmentDTOList,
          setPersonalizedAssignmentListLoading:
            this.setPersonalizedAssignmentListLoading,
          setActivePersonalizedAssignmentList:
            this.setActivePersonalizedAssignmentList,
          setPassivePersonalizedAssignmentList:
            this.setPassivePersonalizedAssignmentList,
          setBranchAssignmentPerformanceLoading:
            this.setBranchAssignmentPerformanceLoading,
          setBranchAssignmentPerformance:
            this.setBranchAssignmentPerformance,
          setLearnerHandwrittenExamPerformanceLoading:
            this.setLearnerHandwrittenExamPerformanceLoading,
          setLearnerHandwrittenExamPerformance:
            this.setLearnerHandwrittenExamPerformance,
          setLearnerMockExamPerformanceLoading:
            this.setLearnerMockExamPerformanceLoading,
          setLearnerMockExamPerformance:
            this.setLearnerMockExamPerformance,
          setLearnerSupportAssignmentPerformanceLoading:
            this.setLearnerSupportAssignmentPerformanceLoading,
          setLearnerSupportAssignmentPerformance:
            this.setLearnerSupportAssignmentPerformance,
          setPersonalizedAssignmentPerformanceLoading:
            this.setPersonalizedAssignmentPerformanceLoading,
          setPersonalizedAssignmentPerformance:
            this.setPersonalizedAssignmentPerformance,
          setBranchAssignmentReportDetailListLoading:
            this.setBranchAssignmentReportDetailListLoading,
          setBranchAssignmentReportDetailDTOList:
            this.setBranchAssignmentReportDetailDTOList,
          setLearnerCompletionOutcomeReportMap:
            this.setLearnerCompletionOutcomeReportMap,
          setLearnerHandwrittenOutcomeReportMap:
            this.setLearnerHandwrittenOutcomeReportMap,
          setLearnerMockExamOutcomeReportMap:
            this.setLearnerMockExamOutcomeReportMap,
          setLearnerSupportAssignmentOutcomeReportMap:
            this.setLearnerSupportAssignmentOutcomeReportMap,
          setReportCategoryDTOList: this.setReportCategoryDTOList,
          setBranchAssignmentCategoryScoreList:
            this.setBranchAssignmentCategoryScoreList,
          setReportDTOList: this.setReportDTOList,
          setIndividualModelDTOList: this.setIndividualModelDTOList,
          setIndividualModelListLoading: this.setIndividualModelListLoading,
          setAvailableFrameworkAnnualPlanList:
            this.setAvailableFrameworkAnnualPlanList,
          setAvailableFrameworkAnnualPlanListLoading:
            this.setAvailableFrameworkAnnualPlanListLoading,
          setFrameworkAnnualPlanWeekOutcomeIdListByCoterieTypeMap:
            this.setFrameworkAnnualPlanWeekOutcomeIdListByCoterieTypeMap,
          setFrameworkAnnualPlanMap: this.setFrameworkAnnualPlanMap,
          setFrameworkAnnualPlanFileMap: this.setFrameworkAnnualPlanFileMap,
          setAnnouncementDTOList: this.setAnnouncementDTOList,
          setAnnouncementListLoading: this.setAnnouncementListLoading,
          setNumberOfUnreadAnnouncements: this.setNumberOfUnreadAnnouncements,
          setContestDTOList: this.setContestDTOList,
          setContestListLoading: this.setContestListLoading,

          setPersonalStatus: this.setPersonalStatus,
          setWeeklyStreak: this.setWeeklyStreak,
          setMyWrongActivityDTOList: this.setMyWrongActivityDTOList,
          setMyWrongActivityListLoading: this.setMyWrongActivityListLoading,
          setActivityReviewDTOList: this.setActivityReviewDTOList,
          setActivityReviewListLoading: this.setActivityReviewListLoading,
          setThisWeekRankingDTOList: this.setThisWeekRankingDTOList,
          setThisWeekRankingListLoading: this.setThisWeekRankingListLoading,
          setAllTimeRankingDTOList: this.setAllTimeRankingDTOList,
          setAllTimeRankingListLoading: this.setAllTimeRankingListLoading,
          setSelfThisWeekRanking: this.setSelfThisWeekRanking,
          setSelfAllTimeRanking: this.setSelfAllTimeRanking,
          setThisWeekBranchRankingDTOList: this.setThisWeekBranchRankingDTOList,
          setAllTimeBranchRankingDTOList: this.setAllTimeBranchRankingDTOList,
          setLeagueItemProgressDTOList: this.setLeagueItemProgressDTOList,
          setLeagueItemProgressListLoading:
            this.setLeagueItemProgressListLoading,
          setTaskItemProgressDTOList:
            this.setTaskItemProgressDTOList,
          setTaskItemProgressListLoading:
            this.setTaskItemProgressListLoading,
          setEarnedAchievementDTOList: this.setEarnedAchievementDTOList,
          setEarnedAchievementListLoading: this.setEarnedAchievementListLoading,
          setNewEarnedAchievementDTO: this.setNewEarnedAchievementDTO,
          setNewEarnedAchievementDTOList: this.setNewEarnedAchievementDTOList,
          resetMascotParams: this.resetMascotParams,
          setMascotMode: this.setMascotMode,
          setShowMascot: this.setShowMascot,
          setShowMascotText: this.setShowMascotText,
          setMascotText: this.setMascotText,
          setMascotSubText: this.setMascotSubText,
          setMascotSubTextList: this.setMascotSubTextList,
          setMascotPrimaryButtonText: this.setMascotPrimaryButtonText,
          setMascotPrimaryButtonOnClick: this.setMascotPrimaryButtonOnClick,
          setMascotChatActivityId: this.setMascotChatActivityId,
          setMascotChatActivityTemplateId: this.setMascotChatActivityTemplateId,
          setMascotChatActivityTemplateType:
            this.setMascotChatActivityTemplateType,
          setMascotChatWelcomeText: this.setMascotChatWelcomeText,
          setMascotChatList: this.setMascotChatList,
          setMascotSendMessageOnClick: this.setMascotSendMessageOnClick,
          setMascotChatLoading: this.setMascotChatLoading,
          setMascotChatCanConvertAudio: this.setMascotChatCanConvertAudio,
          setMascotHasVoiceItem: this.setMascotHasVoiceItem,
          setMascotChatCanDraw: this.setMascotChatCanDraw,
          setMascotChatVoiceType: this.setMascotChatVoiceType,
          setMascotChatActType: this.setMascotChatActType,
          setMascotInventoryItemDTOList: this.setMascotInventoryItemDTOList,
          setMascotInventoryItemListLoading:
            this.setMascotInventoryItemListLoading,
          setMascotPosition: this.setMascotPosition,
          setMascotChatErrorText: this.setMascotChatErrorText,
          setMascotChatContinueText: this.setMascotChatContinueText,
          setMascotChatContinueOnClick: this.setMascotChatContinueOnClick,
          setMascotChatActionList: this.setMascotChatActionList,
          triggerLearnerDataRefresh: this.triggerLearnerDataRefresh,
          setSubjectDescriptionDTOList: this.setSubjectDescriptionDTOList,
          setSubjectDescriptionListLoading:
            this.setSubjectDescriptionListLoading,
          setSubjectDescriptionCompletionDTOList:
            this.setSubjectDescriptionCompletionDTOList,
          setSubjectDescriptionCompletionListLoading:
            this.setSubjectDescriptionCompletionListLoading,
          setNextAchievementProgress: this.setNextAchievementProgress,
          setSummedActionCounts: this.setSummedActionCounts,
          setLearnerGoalProgressList: this.setLearnerGoalProgressList,
          setMarketDTO: this.setMarketDTO,
          setMarketItemPurchaseDTOList: this.setMarketItemPurchaseDTOList,
          setMarketItemPurchaseListLoading:
            this.setMarketItemPurchaseListLoading,
          setWebSocketStompClient: this.setWebSocketStompClient,
          setWebSocketConnectionStatus: this.setWebSocketConnectionStatus,
          setSessionInactiveNoticeOpen: this.setSessionInactiveNoticeOpen,
        }}
      >
        {this.props.children}
      </UserContext.Provider>
    );
  }
}

export const useAuth = (): any => {
  const context = useContext(UserContext);
  if (!context) throw Error("Context must be not null.");
  return context;
};

export default UserContextProvider;
